// Config
import appConfig from '../../config/appConfig';

// React Hooks
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';

// Import esterni
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt, faBars, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faFacebookF, faInstagram, faTwitter, faLinkedin, faPinterest, faYoutube } from '@fortawesome/free-brands-svg-icons';

export interface MenuItem {
    label: string;
    url: string;
    title: string;
    dropdown?: boolean;
    items?: MenuItem[];
}

export interface AgencyConfig {
    agency: {
        customPhone: string;
        customMobile: string;
    };
    socialLinks: {
        whatsapp: string;
        facebook: string | boolean;
        instagram: string | boolean;
        youtube?: string | boolean;
        linkedin: string | boolean;
    };
}

export interface AgencyData {
    agency_name: string;
    phone1: string;
    mobile: string;
    international_prefix: string;
    whatsapp: string | boolean;
    youtube?: string | boolean;
    facebook: string | boolean;
    instagram: string | boolean;
    twitter: string | boolean;
    linkedin: string | boolean;
    pinterest: string | boolean;
    cookie_solution_iubenda: boolean;
}

interface HeaderProps {
    agencyData: AgencyData;
    languageSelector: React.ReactElement;
    assets?: {
        logoPath?: string;
        menuItemsPath?: string;
    };
    className?: string;
    showLoader?: boolean;
    onMenuItemClick?: (item: MenuItem) => void;
}

const Header: React.FC<HeaderProps> = ({
    agencyData,
    languageSelector,
    assets = {
        logoPath: "/assets/images/logoSmall.png",
        menuItemsPath: "/locales"
    },
    className = "",
    showLoader = true,
    onMenuItemClick,
}) => {

    const [loading, setLoading] = useState(showLoader);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [menuLoading, setMenuLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const [collapsed, setCollapsed] = useState(false);
    const [dropDownCollapsed, setDropDownCollapsed] = useState(false);

    console.log('[SharedHeader] agencyData', agencyData);

    useEffect(() => {
        if (!showLoader) {
            setLoading(false);
            return;
        }

        const timer = setTimeout(() => {
            setLoading(false);
        }, 3500);
        return () => clearTimeout(timer);
    }, [showLoader]);

    useEffect(() => {
        const loadMenuItems = async () => {
            const langCode = currentLanguage.split('-')[0];

            if (!langCode || langCode.includes('cimode')) {
                setMenuItems([]);
                setMenuLoading(false);
                return;
            }

            setMenuLoading(true);
            try {
                const response = await fetch(`${assets.menuItemsPath}/${langCode}/menuItems.json`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setMenuItems(data || []);

            } catch (error) {
                console.error(`Failed to load menu items for language: ${langCode}`, error);
                setMenuItems([]);
            } finally {
                setMenuLoading(false);
            }
        };

        if (currentLanguage) {
            loadMenuItems();
        }

    }, [currentLanguage, assets.menuItemsPath]);

    const handleMenuItemClick = (item: MenuItem) => {
        setDropDownCollapsed(false);
        setCollapsed(false);
        onMenuItemClick?.(item);
    };

    return (
        <>
            {/* Loader */}
            {loading && (
                <div id="site-loader" className="load-complete">
                    <div className="load-position">
                        <div className="logo logo-block">
                            <Link to="/" title={`${t('navigation.homeLinkTitle')}`}>
                                <img
                                    src={assets.logoPath}
                                    alt={agencyData.agency_name}
                                    title={agencyData.agency_name}
                                />
                            </Link>
                        </div>
                        <div className="loading">
                            <div className="loading-line"></div>
                            <div className="loading-break loading-dot-1"></div>
                            <div className="loading-break loading-dot-2"></div>
                            <div className="loading-break loading-dot-3"></div>
                        </div>
                    </div>
                </div>
            )}
            {/* /Loader */}

            <header className={`header ${className}`}>

                <div className="topbar">
                    <div className="container-fluid w-special">
                        <div className="row justify-content-between">
                            <div className="col-9 col-lg d-flex flex-column flex-sm-row">
                                {/*
                                {(appConfig.agency.customPhone !== "") || (agencyData.phone1 && agencyData.phone1 !== "N/A") && (
                                    <div className="d-inline-block mr-sm-4">
                                        {appConfig.agency.customPhone !== "" ? (
                                            <a href={`tel:${agencyData.international_prefix + appConfig.agency.customPhone}`} title={`${t('header.topbar.phoneTitle')}`}>
                                                <FontAwesomeIcon icon={faPhone} className="mr-2" style={{ width: "14px" }} />
                                                <span className="phoneNumber">{agencyData.international_prefix + " " + appConfig.agency.customPhone}</span>
                                            </a>
                                        ) : (
                                            <a href={`tel:${agencyData.international_prefix || ''}${agencyData.phone1}`} title={`${t('header.topbar.phoneTitle')}`}>
                                                <FontAwesomeIcon icon={faPhone} className="mr-2" style={{ width: "14px" }} />
                                                <span className="phoneNumber">{`${agencyData.international_prefix || ''} ${agencyData.phone1}`}</span>
                                            </a>
                                        )}
                                    </div>
                                )}
                                */}

                                {(appConfig.agency.customMobile !== "") || (agencyData.mobile && agencyData.mobile !== "N/A") && (
                                    <div className="d-inline-block">
                                        {appConfig.agency.customMobile !== "" ? (
                                            <a href={`tel:${agencyData.international_prefix + appConfig.agency.customMobile}`} title={`${t('header.topbar.mobileTitle')}`}>
                                                <FontAwesomeIcon icon={faMobileAlt} className="mr-2" style={{ width: "14px" }} />
                                                <span className="phoneNumber">{agencyData.international_prefix + " " + appConfig.agency.customMobile}</span>
                                            </a>
                                        ) : (
                                            <a href={`tel:${agencyData.international_prefix || ''}${agencyData.mobile}`} title={`${t('header.topbar.mobileTitle')}`}>
                                                <FontAwesomeIcon icon={faMobileAlt} className="mr-2" style={{ width: "14px" }} />
                                                <span className="phoneNumber">{`${agencyData.international_prefix || ''} ${agencyData.mobile}`}</span>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="col-auto d-flex flex-column flex-sm-row">
                                <div className="d-flex align-items-center">
                                    {/* Social Icons */}
                                    <div className="social">
                                        <ul className="list-unstyled d-flex">
                                            {agencyData.whatsapp && (
                                                <li className="mr-2">
                                                    <a
                                                        title={`${t('footer.social.whatsappTitle')}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        href={'https://web.whatsapp.com/send?phone='+appConfig.socialLinks.whatsapp}
                                                    >
                                                        <FontAwesomeIcon icon={faWhatsapp} />
                                                    </a>
                                                </li>
                                            )}
                                            {agencyData.facebook && typeof appConfig.socialLinks.facebook === 'string' && (
                                                <li className="mr-2">
                                                    <a href={appConfig.socialLinks.facebook} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.facebookTitle')}`}>
                                                        <FontAwesomeIcon icon={faFacebookF} />
                                                    </a>
                                                </li>
                                            )}
                                            {agencyData.instagram && typeof appConfig.socialLinks.instagram === 'string' && (
                                                <li className="mr-2">
                                                    <a href={appConfig.socialLinks.instagram} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.instagramTitle')}`}>
                                                        <FontAwesomeIcon icon={faInstagram} />
                                                    </a>
                                                </li>
                                            )}
                                            {agencyData.youtube && typeof appConfig.socialLinks.youtube === 'string' && (
                                                <li className="mr-2">
                                                    <a href={appConfig.socialLinks.youtube} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.youtubeTitle')}`}>
                                                        <FontAwesomeIcon icon={faYoutube} />
                                                    </a>
                                                </li>
                                            )}
                                            {agencyData.twitter && typeof appConfig.socialLinks.twitter === 'string' && (
                                                <li className="mr-2">
                                                    <a href={appConfig.socialLinks.twitter} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.twitterTitle')}`}>
                                                        <FontAwesomeIcon icon={faTwitter} />
                                                    </a>
                                                </li>
                                            )}
                                            {agencyData.linkedin && typeof appConfig.socialLinks.linkedin === 'string' && (
                                                <li className="mr-2">
                                                    <a href={appConfig.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.linkedinTitle')}`}>
                                                        <FontAwesomeIcon icon={faLinkedin} />
                                                    </a>
                                                </li>
                                            )}
                                            {agencyData.pinterest && typeof appConfig.socialLinks.pinterest === 'string' && (
                                                <li>
                                                    <a href={appConfig.socialLinks.pinterest} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.pinterestTitle')}`}>
                                                        <FontAwesomeIcon icon={faPinterest} />
                                                    </a>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                    {/* Language Selector */}
                                    {languageSelector}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <nav className="navbar navbar-static-top navbar-expand-lg header-sticky">

                    <div className="container-fluid w-special">
                        {/* Bottone Hamburger */}
                        <button
                            type="button"
                            className={`navbar-toggler ${!collapsed ? "" : "collapsed"}`}
                            aria-controls="navbarCollapse"
                            aria-expanded="false"
                            aria-label="Toggle navigation"
                            onClick={() => { setDropDownCollapsed(false); setCollapsed(prev => !prev); }}
                        >
                            <FontAwesomeIcon icon={faBars} />
                        </button>

                        {/* Logo Navbar */}
                        <div className='d-flex align-items-center'> 
                            <Link className="navbar-brand" to="/" title={t('navigation.homeLinkTitle') as string}>
                                <img
                                    className="img-fluid"
                                    src={assets.logoPath}
                                    alt={`${t('header.navbar.logoAlt')}`}
                                />
                            </Link>
                        </div>

                        {/* Menu Collapse */}
                        <div className={`navbar-collapse collapse ${!collapsed ? "" : "show"}`} id="navbarCollapse">
                            {menuLoading ? (
                                <div className="navbar-nav"><span className="nav-link">{t('common.loading')}...</span></div>
                            ) : (
                                <ul className="nav navbar-nav">
                                    {menuItems.map((menuItem, index) => (
                                        <li key={index} className={`nav-item ${menuItem.dropdown ? 'dropdown' : ''}`}>
                                            {menuItem.url && menuItem.url !== '#' && !menuItem.dropdown ? (
                                                <Link
                                                    to={menuItem.url}
                                                    title={menuItem.title}
                                                    className={`nav-link`}
                                                    onClick={() => handleMenuItemClick(menuItem)}
                                                >
                                                    {menuItem.label}
                                                </Link>
                                            ) : (
                                                <a
                                                    href={menuItem.url || '#'}
                                                    title={menuItem.title}
                                                    className={`nav-link ${menuItem.dropdown ? 'dropdown-toggle' : ''}`}
                                                    role={menuItem.dropdown ? 'button' : undefined}
                                                    aria-haspopup={menuItem.dropdown ? true : undefined}
                                                    aria-expanded={menuItem.dropdown ? false : undefined}
                                                    onClick={(e) => { e.preventDefault(); setDropDownCollapsed(prev => !prev) }}
                                                >
                                                    {menuItem.label}
                                                    {menuItem.dropdown && (
                                                        <span className="dropdownchevron ml-1">
                                                            <FontAwesomeIcon icon={faChevronDown} size="xs" />
                                                        </span>
                                                    )}
                                                </a>
                                            )}
                                            {/* Dropdown Submenu */}
                                            {menuItem.dropdown && menuItem.items && (
                                                <ul className={`dropdown-menu ${!dropDownCollapsed ? "" : "show"}`} aria-labelledby={`navbarDropdown${index}`}>
                                                    {menuItem.items.map((item, subIndex) => (
                                                        <li key={subIndex}>
                                                            {item.url && !item.url.startsWith('http') ? (
                                                                <Link
                                                                    to={item.url}
                                                                    title={item.title}
                                                                    className="dropdown-item"
                                                                    onClick={() => handleMenuItemClick(item)}
                                                                >
                                                                    {item.label}
                                                                </Link>
                                                            ) : (
                                                                <a
                                                                    href={item.url || '#'}
                                                                    title={item.title}
                                                                    className="dropdown-item"
                                                                    target={item.url && item.url.startsWith('http') ? '_blank' : undefined}
                                                                    rel={item.url && item.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                                    onClick={() => handleMenuItemClick(item)}
                                                                >
                                                                    {item.label}
                                                                </a>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <Link
                            to={'/valutazione'}
                            title={t('navigation.valutationLinkTitle') as string}
                            className="d-none d-sm-block mr-sm-5 mr-lg-0 btn btn-primary"
                        >
                            {t('navigation.valutationLinkTitle') as string}
                        </Link>
                    </div>
                </nav>
            </header>
        </>
    );
};

export default Header;
